import { useCallback, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { saveAs } from 'file-saver';

import toast from 'components/partials/toast/toast';
import type { NormalizedBarChartData } from 'components/partials/charts/shared/types';
import { barChartToCsv } from 'components/partials/charts/shared/bar-chart-to-csv';
import { CHART_AXIS } from 'components/partials/charts/shared/chart-tokens';

import {
  buildPngExportFileName,
  captureElementToPng,
  savePngDataUrl,
} from 'helpers/png-export';

export type ChartExportFormat = 'png' | 'csv';

const DEFAULT_FILE_NAME = 'chart';

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const TICK_LABEL_SELECTOR = '[data-chart-x-tick-label="true"]';

/**
 * Recharts renders horizontal labels inside HTML-in-SVG <foreignObject> wrappers,
 * which get clipped during rasterization. We temporarily swap them for native
 * SVG <text> elements for the duration of the capture, then restore the DOM.
 */
export const withSvgTextTickLabels = (element: HTMLElement): (() => void) => {
  const restorers: Array<() => void> = [];

  element
    .querySelectorAll<HTMLElement>(TICK_LABEL_SELECTOR)
    .forEach((label) => {
      const foreignObject =
        label.closest<SVGForeignObjectElement>('foreignObject');
      const group = foreignObject?.parentNode;
      const text = label.textContent?.trim();

      if (!foreignObject || !group || !text) {
        return;
      }

      const foreignX = Number.parseFloat(
        foreignObject.getAttribute('x') ?? '0'
      );
      const foreignY = Number.parseFloat(
        foreignObject.getAttribute('y') ?? '0'
      );
      const foreignWidth = Number.parseFloat(
        foreignObject.getAttribute('width') ?? '0'
      );

      const targetCenterY =
        label.getBoundingClientRect().top +
        label.getBoundingClientRect().height / 2;

      const svgText = document.createElementNS(SVG_NAMESPACE, 'text');

      svgText.setAttribute('x', String(foreignX + foreignWidth / 2));
      svgText.setAttribute('y', String(foreignY));
      svgText.setAttribute('text-anchor', 'middle');
      svgText.setAttribute('dominant-baseline', 'middle');
      svgText.setAttribute('fill', CHART_AXIS.tickColor);
      svgText.setAttribute('font-family', CHART_AXIS.tickFontFamily);
      svgText.setAttribute('font-size', String(CHART_AXIS.tickFontSize));
      svgText.textContent = text;

      group.appendChild(svgText);

      // Render the svg then correct correct the text's position to match the original text's vertical center
      const renderedCenterY =
        svgText.getBoundingClientRect().top +
        svgText.getBoundingClientRect().height / 2;

      svgText.setAttribute(
        'y',
        String(foreignY + (targetCenterY - renderedCenterY))
      );

      const previousDisplay = foreignObject.style.display;

      foreignObject.style.display = 'none';

      restorers.push(() => {
        svgText.remove();
        foreignObject.style.display = previousDisplay;
      });
    });

  return () => restorers.forEach((restore) => restore());
};

/** Compile-time exhaustiveness guard for ChartExportFormat switch branches. */
const assertNever = (value: never): never => {
  throw new Error(`Unexpected chart export format: ${String(value)}`);
};

const captureChartToPng = async (element: HTMLElement): Promise<string> => {
  const restoreTickLabels = withSvgTextTickLabels(element);

  try {
    return await captureElementToPng(element);
  } finally {
    restoreTickLabels();
  }
};

interface UseChartExportArgs {
  targetRef: RefObject<HTMLElement | null>;
  data: NormalizedBarChartData | null;
  fileName?: string;
}

export interface UseChartExportResult {
  exportChart: (format: ChartExportFormat) => Promise<boolean>;
  isExporting: boolean;
}

export const useChartExport = ({
  targetRef,
  data,
  fileName = DEFAULT_FILE_NAME,
}: UseChartExportArgs): UseChartExportResult => {
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const isExportingRef = useRef(false);

  const exportChart = useCallback(
    async (format: ChartExportFormat): Promise<boolean> => {
      if (isExportingRef.current) {
        return false;
      }

      const baseName = buildPngExportFileName(fileName, DEFAULT_FILE_NAME);

      isExportingRef.current = true;
      setIsExporting(true);

      try {
        switch (format) {
          case 'csv': {
            if (!data) {
              toast.error({
                title: 'Export Failed',
                message: 'Data not found. Please try again.',
              });

              return false;
            }

            const csvBlob = new Blob([barChartToCsv(data)], {
              type: 'text/csv;charset=utf-8',
            });

            saveAs(csvBlob, `${baseName}.csv`);
            break;
          }

          case 'png': {
            const element = targetRef.current;

            if (!element) {
              toast.error({
                title: 'Export Failed',
                message: 'Chart not found. Please try again.',
              });

              return false;
            }

            await savePngDataUrl(await captureChartToPng(element), baseName);
            break;
          }

          default:
            assertNever(format);
        }

        return true;
      } catch (error) {
        console.error(`Error exporting chart ${format.toUpperCase()}:`, error);
        toast.error({
          title: 'Export Failed',
          message: `An error occurred while exporting the ${format.toUpperCase()}.`,
        });

        return false;
      } finally {
        isExportingRef.current = false;
        setIsExporting(false);
      }
    },
    [data, fileName, targetRef]
  );

  return {
    exportChart,
    isExporting,
  };
};
